/**
 * Professionals List Page
 * Main page for listing and managing healthcare professionals
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  ToggleOff as InactivateIcon,
  ToggleOn as ActivateIcon,
  LocalHospital as MedicoIcon,
  Engineering as EngenheiroIcon,
  Psychology as PsicologoIcon,
  Healing as EnfermeiroIcon,
  Science as TecnicoIcon,
  Person as DefaultIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

// Components
import ProfessionalFilters from './components/ProfessionalFilters';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import { RouteActionButton } from '../../components/common';

// Services and types
import { professionalService } from '../../services/professionalService';
import {
  Professional,
  ProfessionalFilters as ProfessionalFiltersType,
  ProfessionalType,
  PROFESSIONAL_TYPE_LABELS,
} from '../../types/professional';

// Regras de exibição compartilhadas com a ficha do profissional
import {
  STATUS_COLORS,
  formatCPF,
  getProfessionalType,
  getRegistration,
  isMetaTruthy,
  isProfessionalActive,
} from './professionalDisplay';

interface ProfessionalFiltersData {
  searchTerm: string;
  status: string[];
  professionalTypes: string[];
  isExaminer: boolean;
}

// Type colors
const TYPE_COLORS: Record<ProfessionalType, 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error' | 'default'> = {
  [ProfessionalType.MEDICO_TRABALHO]: 'primary',
  [ProfessionalType.ENFERMEIRO]: 'info',
  [ProfessionalType.TECNICO_ENFERMAGEM]: 'info',
  [ProfessionalType.ENGENHEIRO_SEGURANCA]: 'warning',
  [ProfessionalType.ENGENHEIRO_ELETRICISTA]: 'warning',
  [ProfessionalType.ENGENHEIRO_MECANICO]: 'warning',
  [ProfessionalType.TECNICO_SEGURANCA]: 'warning',
  [ProfessionalType.FONOAUDIOLOGO]: 'secondary',
  [ProfessionalType.PSICOLOGO]: 'secondary',
  [ProfessionalType.MEDICO_EXAMINADOR]: 'success',
  [ProfessionalType.ERGONOMISTA]: 'default',
  [ProfessionalType.OUTRO]: 'default',
};

// Get icon for professional type
const getTypeIcon = (type?: string) => {
  const icons: Record<string, React.ReactElement> = {
    [ProfessionalType.MEDICO_TRABALHO]: <MedicoIcon fontSize="small" />,
    [ProfessionalType.MEDICO_EXAMINADOR]: <MedicoIcon fontSize="small" />,
    [ProfessionalType.ENFERMEIRO]: <EnfermeiroIcon fontSize="small" />,
    [ProfessionalType.TECNICO_ENFERMAGEM]: <EnfermeiroIcon fontSize="small" />,
    [ProfessionalType.ENGENHEIRO_SEGURANCA]: <EngenheiroIcon fontSize="small" />,
    [ProfessionalType.ENGENHEIRO_ELETRICISTA]: <EngenheiroIcon fontSize="small" />,
    [ProfessionalType.ENGENHEIRO_MECANICO]: <EngenheiroIcon fontSize="small" />,
    [ProfessionalType.TECNICO_SEGURANCA]: <TecnicoIcon fontSize="small" />,
    [ProfessionalType.PSICOLOGO]: <PsicologoIcon fontSize="small" />,
  };
  return icons[type || ''] || <DefaultIcon fontSize="small" />;
};

const columns: DataTableColumn<Professional>[] = [
  {
    id: 'professional',
    label: 'Profissional',
    primary: true,
    render: (professional) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
          {getTypeIcon(getProfessionalType(professional))}
        </Avatar>
        <Box>
          <Typography variant="subtitle2" fontWeight="bold">
            {professional.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {professional.email}
          </Typography>
        </Box>
      </Box>
    ),
  },
  {
    id: 'cpf',
    label: 'CPF',
    render: (professional) => (
      <Typography variant="body2" fontFamily="monospace">
        {formatCPF(professional.cpf)}
      </Typography>
    ),
  },
  {
    id: 'type',
    label: 'Tipo',
    render: (professional) => {
      const type = getProfessionalType(professional);
      if (!type) {
        return <Typography variant="body2" color="text.secondary">-</Typography>;
      }
      const label = PROFESSIONAL_TYPE_LABELS[type as ProfessionalType] || type;
      return (
        <Tooltip title={label}>
          <Chip
            icon={getTypeIcon(type)}
            label={label}
            size="small"
            color={TYPE_COLORS[type as ProfessionalType] || 'default'}
            variant="outlined"
          />
        </Tooltip>
      );
    },
  },
  {
    id: 'registration',
    label: 'Registro',
    render: (professional) => (
      <>
        <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
          {getRegistration(professional)}
        </Typography>
        {professional.meta?.specialty && (
          <Typography variant="caption" color="text.secondary" display="block">
            {professional.meta.specialty}
          </Typography>
        )}
      </>
    ),
  },
  {
    id: 'responsibilities',
    label: 'Responsabilidades',
    render: (professional) => (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {isMetaTruthy(professional.meta?.isExaminer) && (
          <Tooltip title="Médico Examinador">
            <Chip label="Examinador" size="small" color="info" variant="outlined" />
          </Tooltip>
        )}
        {isMetaTruthy(professional.meta?.isMedicalResponsible) && (
          <Tooltip title="Médico Responsável">
            <Chip label="Méd. Resp." size="small" color="success" variant="outlined" />
          </Tooltip>
        )}
        {isMetaTruthy(professional.meta?.isTechnicalResponsible) && (
          <Tooltip title="Responsável Técnico">
            <Chip label="RT" size="small" color="primary" variant="outlined" />
          </Tooltip>
        )}
        {!isMetaTruthy(professional.meta?.isExaminer) &&
         !isMetaTruthy(professional.meta?.isMedicalResponsible) &&
         !isMetaTruthy(professional.meta?.isTechnicalResponsible) && (
          <Typography variant="body2" color="text.secondary">-</Typography>
        )}
      </Box>
    ),
  },
  {
    id: 'status',
    label: 'Status',
    render: (professional) => (
      <Chip
        label={professional.situacao}
        size="small"
        color={STATUS_COLORS[professional.situacao] || 'default'}
      />
    ),
  },
];

const ProfessionalsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const visualizar = useControle('/professionals/:id', pode('professional.read'), 'Visualizar');
  const editar = useControle('/professionals/:id/edit', pode('professional.update'), 'Editar');
  const inativar = useAcao('professional.update', 'Inativar');
  const ativar = useAcao('professional.update', 'Ativar');
  const excluir = useAcao('professional.delete', 'Excluir');

  // State
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [currentFilters, setCurrentFilters] = useState<Partial<ProfessionalFiltersType>>({ status: 'Ativo' });
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog states
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [professionalToToggle, setProfessionalToToggle] = useState<Professional | null>(null);
  const [professionalToDelete, setProfessionalToDelete] = useState<Professional | null>(null);

  // Load professionals from API
  const loadProfessionals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await professionalService.getProfessionals({
        page,
        limit: rowsPerPage,
        search: searchTerm,
        ...currentFilters,
      });
      setProfessionals(response.data);
      setTotalCount(response.pagination.total);
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = (apiError.response?.data?.error || apiError.message) ?? 'Erro ao carregar profissionais';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, currentFilters, enqueueSnackbar]);

  // Apply filters
  const applyFilters = (filters: ProfessionalFiltersData) => {
    const apiFilters: Partial<ProfessionalFiltersType> = {};

    if (filters.status.length > 0) {
      apiFilters.status = filters.status.join(',');
    }
    if (filters.professionalTypes.length > 0) {
      apiFilters.professionalType = filters.professionalTypes.join(',');
    }
    if (filters.isExaminer) {
      apiFilters.isExaminer = true;
    }

    setCurrentFilters(apiFilters);
    setSearchTerm(filters.searchTerm);
    setPage(1); // Reset to first page when filtering
  };

  // Handle view professional
  const handleViewProfessional = (professional: Professional) => {
    navigate(`/professionals/${professional.id}`);
  };

  // Handle edit professional
  const handleEditProfessional = (professional: Professional) => {
    navigate(`/professionals/${professional.id}/edit`);
  };

  // Handle toggle professional status
  const handleToggleStatus = async (professional: Professional) => {
    try {
      const professionalId = professional.id.toString();
      await professionalService.toggleProfessionalStatus(professionalId);
      const isActive = professional.situacao === 'Ativo';
      enqueueSnackbar(`Profissional ${isActive ? 'inativado' : 'ativado'} com sucesso`, { variant: 'success' });
      loadProfessionals(); // Reload data
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { error?: string } } };
      const errorMessage = apiError.response?.data?.error || 'Erro ao alterar status do profissional';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // Handle delete professional
  const handleDeleteProfessional = async (professional: Professional) => {
    try {
      const professionalId = professional.id.toString();
      await professionalService.deleteProfessional(professionalId);
      enqueueSnackbar('Profissional excluído com sucesso', { variant: 'success' });
      loadProfessionals(); // Reload data
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { error?: string } } };
      const errorMessage = apiError.response?.data?.error || 'Erro ao excluir profissional';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // Confirmation dialog handlers
  const handleToggleStatusClick = (professional: Professional) => {
    setProfessionalToToggle(professional);
    setToggleDialogOpen(true);
  };

  const handleDeleteClick = (professional: Professional) => {
    setProfessionalToDelete(professional);
    setDeleteDialogOpen(true);
  };

  const handleToggleStatusConfirm = () => {
    if (professionalToToggle) {
      handleToggleStatus(professionalToToggle);
    }
    setToggleDialogOpen(false);
    setProfessionalToToggle(null);
  };

  const handleDeleteConfirm = () => {
    if (professionalToDelete) {
      handleDeleteProfessional(professionalToDelete);
    }
    setDeleteDialogOpen(false);
    setProfessionalToDelete(null);
  };

  // Load data on mount and when dependencies change
  useEffect(() => {
    loadProfessionals();
  }, [loadProfessionals]);

  // "Ativo" já vem marcado ao abrir a tela: só conta como filtro o que a pessoa mudou.
  const filtersActive =
    searchTerm.trim() !== '' ||
    (currentFilters.status ?? 'Ativo') !== 'Ativo' ||
    Boolean(currentFilters.professionalType) ||
    Boolean(currentFilters.isExaminer);

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Profissionais' },
      ]}
      title="Profissionais"
      actions={
        <RouteActionButton
          para="/professionals/create"
          exige={pode('professional.create')}
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
        >
          Novo Profissional
        </RouteActionButton>
      }
    >
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <ProfessionalFilters onFilter={applyFilters} loading={loading} total={totalCount} />
      </Box>

      {/* Professionals Table */}
      <DataTable
        columns={columns}
        rows={professionals}
        getRowId={(professional) => professional.id}
        loading={loading}
        emptyState={{
          title: 'Nenhum profissional vinculado ainda',
          description:
            'Profissionais são os médicos e técnicos que atendem pelo prestador; cada um precisa estar vinculado a um prestador.',
          prereqChain: 'professionals',
          action: {
            label: 'Cadastrar profissional',
            node: 'professionals',
            deniedHint: 'Peça ao administrador da sua clínica para cadastrar um profissional.',
          },
        }}
        filtersActive={filtersActive}
        actions={[
          {
            icon: <VisibilityIcon fontSize="small" />,
            onClick: handleViewProfessional,
            ...visualizar,
          },
          {
            icon: <EditIcon fontSize="small" />,
            onClick: handleEditProfessional,
            ...editar,
          },
          {
            icon: <InactivateIcon fontSize="small" />,
            color: 'warning',
            onClick: handleToggleStatusClick,
            hidden: (professional) => !isProfessionalActive(professional),
            ...inativar,
          },
          {
            icon: <ActivateIcon fontSize="small" />,
            color: 'success',
            onClick: handleToggleStatusClick,
            hidden: (professional) => isProfessionalActive(professional),
            ...ativar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: handleDeleteClick,
            ...excluir,
          },
        ]}
        pagination={{
          page: page - 1,
          rowsPerPage,
          totalCount,
          onPageChange: (newPage) => setPage(newPage + 1),
          onRowsPerPageChange: (newRowsPerPage) => {
            setPage(1);
            setRowsPerPage(newRowsPerPage);
          },
        }}
      />

      {/* Toggle Status Confirmation Dialog */}
      <Dialog open={toggleDialogOpen} onClose={() => setToggleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirmar {professionalToToggle && isProfessionalActive(professionalToToggle) ? 'Inativação' : 'Ativação'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja{' '}
            {professionalToToggle && isProfessionalActive(professionalToToggle) ? 'inativar' : 'ativar'} o profissional{' '}
            <strong>{professionalToToggle?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {professionalToToggle && isProfessionalActive(professionalToToggle)
              ? 'O profissional será marcado como inativo e não aparecerá nas listagens padrão.'
              : 'O profissional será marcado como ativo e voltará a aparecer nas listagens.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToggleDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleToggleStatusConfirm}
            color={professionalToToggle && isProfessionalActive(professionalToToggle) ? 'warning' : 'success'}
            variant="contained"
          >
            {professionalToToggle && isProfessionalActive(professionalToToggle) ? 'Inativar' : 'Ativar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>
          Confirmar Exclusão
        </DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o profissional <strong>{professionalToDelete?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Esta ação não poderá ser desfeita!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default ProfessionalsListPage;
